// Photo composition: single shots and frame strips. Runs in the browser (live
// watermarked previews) and on the server (paid HD / Ultra renders) — same code,
// so what people see in the room is exactly what they buy.
// v0.5: the people area is composed by still.ts with the same placement, layer
// decision (captured at the shutter), part masks and colour matching as the live
// shared mirror (render/stage.ts).

import { makeCanvas, type AnyCanvas, type Ctx2D } from './canvas';
import { sceneSlotOf, type FaceBox, type PoseAdjust } from './layout';
import type { PoseFrame } from './pose';
import type { Decision } from './solver';
import { drawPeople } from './still';
import {
  drawBackground, drawFramePaper, drawStickerSet, filterById, frameColorById, layoutById,
  type BackgroundId, type FilterId, type FrameColorId, type LayoutId,
} from './themes';
import { SHOT_BASE } from './tiers';

/** Font stacks incl. symbol/emoji fallbacks the server registers (server/fonts). */
const FONTS_DISPLAY = '"Baloo 2", "Quicksand", "Noto Sans Symbols 2", "Noto Emoji", sans-serif';
const FONTS_TEXT = '"Quicksand", "Noto Sans Symbols 2", "Noto Emoji", sans-serif';

type Img = CanvasImageSource & { width: number; height: number };

export interface PersonShot {
  /** RGBA cutout (or full frame when `full`), un-mirrored camera orientation */
  image: Img;
  face: FaceBox;
  adjust: PoseAdjust;
  slot: number;
  /** informational since v0.5: the whole people area follows `mirrorPhoto` */
  mirrored?: boolean;
  /** no face in frame (rear camera filming the scenery) */
  noFace?: boolean;
  /** v0.5: peer id (keys of the decision) */
  id?: string;
  /** v0.5: pose of the captured frame (keypoints normalised to the image, un-mirrored) */
  pose?: PoseFrame | null;
  /** v0.5: layer decision frozen at the shutter on this person's device (the host's wins) */
  decision?: Decision | null;
}

export interface StripOptions {
  layout: LayoutId;
  frameColor: FrameColorId;
  background: BackgroundId;
  /** with background 'member-camera': whose camera is the scene */
  sceneSlot?: number;
  customBackground?: Img | null;
  filter: FilterId;
  caption: string;
  date: Date;
  stickers: boolean;
  /** people area mirrored as seen live (default true; text/frame never mirrored) */
  mirrorPhoto?: boolean;
}

export interface RenderOptions {
  scale?: number;
  watermark?: boolean;
}

/** Renders one cell (background + people) at the given size, without filter. */
export function renderCell(w: number, h: number, people: PersonShot[], opt: StripOptions): AnyCanvas {
  const c = makeCanvas(w, h);
  const ctx = c.getContext('2d')! as Ctx2D;
  drawBackground(ctx, opt.background, w, h, opt.customBackground);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  drawPeople(ctx, w, h, people, sceneSlotOf(opt), opt.mirrorPhoto);
  return c;
}

/** Applies the colour filter (same CSS filter string used by the live preview). */
function drawFiltered(ctx: Ctx2D, src: AnyCanvas, x: number, y: number, w: number, h: number, filterId: FilterId) {
  const f = filterById(filterId);
  ctx.save();
  ctx.filter = f.css;
  ctx.drawImage(src, x, y, w, h);
  ctx.restore();
  if (f.tint) {
    ctx.fillStyle = f.tint;
    ctx.fillRect(x, y, w, h);
  }
  if (f.grain) {
    const img = ctx.getImageData(x, y, w, h);
    const d = img.data;
    let seed = 7;
    for (let i = 0; i < d.length; i += 4) {
      seed = (seed * 1103515245 + 12345) >>> 0;
      const n = ((seed >>> 16) / 65535 - 0.5) * 255 * f.grain;
      d[i] += n; d[i + 1] += n; d[i + 2] += n;
    }
    ctx.putImageData(img, x, y);
  }
}

/** Diagonal repeated "PREVIEW" watermark used for the free tier. */
export function drawWatermark(ctx: Ctx2D, w: number, h: number) {
  ctx.save();
  const size = Math.max(14, Math.round(Math.min(w, h) * 0.06));
  ctx.font = `800 ${size}px ${FONTS_DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.translate(w / 2, h / 2);
  ctx.rotate(-Math.PI / 6);
  const text = 'BOOTH TOGETHER · PREVIEW';
  const step = ctx.measureText(text).width + size * 2;
  const diag = Math.hypot(w, h);
  for (let row = 0, y = -diag / 2; y < diag / 2; row++, y += size * 3.2) {
    for (let x = -diag / 2 + (row % 2) * step / 2; x < diag / 2; x += step) {
      ctx.fillStyle = 'rgba(0,0,0,0.10)';
      ctx.fillText(text, x + 1, y + 1);
      ctx.fillStyle = 'rgba(255,255,255,0.34)';
      ctx.fillText(text, x, y);
    }
  }
  ctx.restore();
}

/** One shot as a stand-alone 4:3 photo. */
export function composeShot(people: PersonShot[], opt: StripOptions, r: RenderOptions = {}): AnyCanvas {
  const scale = r.scale ?? 1;
  const w = SHOT_BASE.w * scale, h = SHOT_BASE.h * scale;
  const cell = renderCell(w, h, people, opt);
  const out = makeCanvas(w, h);
  const ctx = out.getContext('2d')! as Ctx2D;
  drawFiltered(ctx, cell, 0, 0, w, h, opt.filter);
  if (r.watermark) drawWatermark(ctx, w, h);
  return out;
}

/** Builds the final strip. `shots[i]` = the people captured for cell i. */
export function composeStrip(shots: PersonShot[][], opt: StripOptions, r: RenderOptions = {}): AnyCanvas {
  const k = r.scale ?? 1;
  const L = layoutById(opt.layout);
  const fc = frameColorById(opt.frameColor);
  const W = L.width * k, H = L.height * k;
  const out = makeCanvas(W, H);
  const ctx = out.getContext('2d')! as Ctx2D;

  // frame paper: colour + pattern (seasonal packs bring their own)
  drawFramePaper(ctx, fc, W, H, k);

  L.cells.forEach((c, i) => {
    const people = shots[i] ?? [];
    const x = c.x * k, y = c.y * k, w = c.w * k, h = c.h * k;
    const cell = renderCell(w, h, people, opt);
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, Math.round(w * 0.015));
    ctx.clip();
    drawFiltered(ctx, cell, x, y, w, h, opt.filter);
    ctx.restore();
  });

  // footer: caption + date, the classic Korean booth look
  const f = L.footer;
  ctx.fillStyle = fc.fg;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const big = Math.round(Math.min(f.h * 0.28, L.width * 0.075) * k);
  ctx.font = `700 ${big}px ${FONTS_DISPLAY}`;
  ctx.fillText(opt.caption || 'together ♡', (f.x + f.w / 2) * k, (f.y + f.h * 0.42) * k);
  ctx.font = `500 ${Math.round(big * 0.42)}px ${FONTS_TEXT}`;
  const d = opt.date;
  const p2 = (n: number) => String(n).padStart(2, '0');
  ctx.fillText(`${d.getFullYear()}.${p2(d.getMonth() + 1)}.${p2(d.getDate())}  ·  booth together`, (f.x + f.w / 2) * k, (f.y + f.h * 0.72) * k);

  if (opt.stickers) drawStickerSet(ctx, fc.sticker ?? 'classic', W, H, fc.fg);
  if (r.watermark) drawWatermark(ctx, W, H);
  return out;
}
